import React, { useMemo } from 'react';
import { ExclamationCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Button, Form, Divider, message, Tooltip, App } from 'antd';
import * as Yup from 'yup';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ApplicationState } from '../../../reducers';
import { AsyncDispatch } from '../../../../types/global';
import { booleanToInt } from '../../../utils/Utils';
import { createUser, resetUserPassword, updateMappedUserPermissions } from '../../../actions/user';
import { ListUser, UserPermissionRole } from '../../../../types/user';
import FormInput from '../../global/Forms/FormInput';
import FormSelect from '../../global/Forms/FormSelect';
import FormMultiSelect from '../../global/Forms/FormMultiSelect';
import DrawerHookForm from '../../global/drawer/DrawerHookForm';
import FormSwitch from '../../global/Forms/FormSwitch';
import AntTooltip from '../../global/AntTooltip';
import AntSelect from '../../global/AntSelect';
import { StandardResourceCode } from '../../../../types/resources';
import { Brand } from '../../../../types/brand';
import { isReceiver } from '../../../utils/UserUtils';
import { hasPermission } from '../../../utils/Permissions';
import { activeBrands } from '../../../selectors/brand/brandSelector';
import { useAppDispatch } from '../../../store/ReduxStore';

type UserDrawerProps = {
  visible: boolean;
  onClose: () => void;
  currentUserId: number;
  user?: ListUser;
  updateUserState: (active: number) => Promise<any>;
  showRoleDrawer: () => void;
};

const UserDrawer: React.FC<UserDrawerProps> = props => {
  const dispatch: AsyncDispatch = useAppDispatch();
  const { t } = useTranslation();
  const { modal } = App.useApp();

  const { roles, maintenanceTypes, brands, receiverUser, canManageUsers } = useSelector(
    (state: ApplicationState) => ({
      roles: state.user.roles as UserPermissionRole[],
      maintenanceTypes: state.resources.data.global.maintenance_types as StandardResourceCode[],
      brands: activeBrands(state) as Brand[],
      receiverUser: state.user.user && isReceiver(state.user.user),
      canManageUsers: hasPermission(state.user.user, 'can_manage_users') as boolean,
    })
  );

  const extendedMaintenanceTypes = useMemo(
    () => [...maintenanceTypes, { id: 3, code: '', name: 'Hide' }],
    [maintenanceTypes]
  );

  const handleUserChange = async (values: any, setError: any) => {
    const { user } = props;
    const { firstName, lastName, email, emailNotification, password, admin, roles, brands } =
      values;

    const brandPermissions = Object.keys(brands)
      .filter(key => brands[Number(key)] !== 3)
      .map(key => ({
        brand_id: Number(key),
        maintenance_type_id: brands[Number(key)],
      }));

    if (user) {
      await dispatch(
        updateMappedUserPermissions({
          id: user.id,
          first_name: firstName,
          last_name: lastName,
          email_notification: emailNotification,
          admin: booleanToInt(admin),
          user_role_ids: roles,
          brand_permissions: brandPermissions,
        })
      );
      props.onClose();
    }

    if (!user)
      await dispatch(
        createUser({
          emailNotification,
          firstName,
          lastName,
          email,
          password,
          admin: booleanToInt(admin)!,
        })
      )
        // Update should be removed when create controller is updated
        .then(result =>
          dispatch(
            updateMappedUserPermissions({
              id: result.value.data.id,
              first_name: firstName,
              last_name: lastName,
              admin: booleanToInt(admin),
              user_role_ids: roles,
              brand_permissions: brandPermissions,
              email_notification: emailNotification,
            })
          ).then(() => props.onClose())
        )
        .catch(result => {
          const message = result.response.data.message;
          const status = result.response.status;
          if (message && status === 422) setError('email', { type: 'manual', message });
        });
  };

  const handleUserStateChange = () => {
    const { user, updateUserState, onClose } = props;
    const active = user && user.active ? 0 : 1;

    modal.confirm({
      title: !active ? t('user:disableUser') : t('user:enableUser'),
      icon: <ExclamationCircleOutlined />,
      async onOk() {
        const userUpdateResponse = await updateUserState(active);
        onClose();
        return userUpdateResponse;
      },
    });
  };

  const handlePasswordReset = () => {
    const { user } = props;
    const email = (user && user.email) || '';
    dispatch(resetUserPassword(email)).then(() => {
      message.info(t('user:resetPasswordMessage'));
      props.onClose();
    });
  };

  const brandSelectState = (values: { [key: number]: number }) => {
    const maintenanceTypeIds = Object?.values(values);
    const uniq = [...new Set(maintenanceTypeIds)];
    return uniq?.length === 1 ? uniq[0]?.toString() : undefined;
  };

  const getInitialBrandValues = () => {
    const { user } = props;

    const brandPermissions = brands.map(brand => {
      const perm = user ? user.brand_permissions.find(b => b.brand_id === brand.id) : undefined;
      return {
        brandId: brand.id,
        maintenanceTypeId: perm ? perm.maintenance_type_id : 3,
      };
    });

    const brandObj = brandPermissions.reduce(
      (obj, item) => ({ ...obj, [item.brandId]: item.maintenanceTypeId }),
      {}
    );

    return brandObj;
  };

  const getValidationSchema = () => {
    return Yup.object().shape({
      firstName: Yup.string()
        .min(2, t('validation:tooShort'))
        .max(50, t('validation:tooLong'))
        .required(t('validation:required')),
      lastName: Yup.string()
        .min(2, t('validation:tooShort'))
        .max(50, t('validation:tooLong'))
        .required(t('validation:required')),
      email: Yup.string().email(t('validation:invalidEmail')).required(t('validation:required')),
      password: props.user
        ? Yup.string()
        : Yup.string()
            .required(t('validation:required'))
            .min(12, t('validation:passwordLength', { length: 12 }))
            .matches(/[0-9]/, t('validation:passwordRequiresNumber'))
            .matches(/[a-z]/, t('validation:passwordRequiresLowercase'))
            .matches(/[A-Z]/, t('validation:passwordRequiresUppercase'))
            .matches(/[^\w]/, t('validation:passwordRequiresSpecial')),
      admin: Yup.bool().required(t('validation:required')),
    });
  };

  const initialValues = () => {
    return {
      firstName: props.user ? props.user.first_name : '',
      lastName: props.user ? props.user.last_name : '',
      emailNotification: props.user ? props.user.email_notification : 0,
      email: props.user ? props.user.email : '',
      password: '',
      admin: props.user ? !!props.user.admin : receiverUser,
      brands: getInitialBrandValues(),
      roles: props.user ? props.user.user_role_ids : [],
    };
  };

  return (
    <DrawerHookForm
      key={props.user?.id || 'new-user'}
      title={props.user ? `${props.user.first_name} ${props.user.last_name}` : t('user:addUser')}
      width={500}
      visible={props.visible}
      onClose={props.onClose}
      destroyOnClose
      onSubmit={(values, setError) => handleUserChange(values, setError)}
      handleSaveButtonEnabled={formState => canManageUsers && formState.isDirty}
      defaultValues={initialValues()}
      validationSchema={getValidationSchema()}
    >
      {({ setValue, watch }) => {
        const values = watch();
        const handleBrandSelect = (maintenanceTypeId: number) => {
          brands.forEach(brand =>
            setValue(`brands.${brand.id}`, maintenanceTypeId, { shouldDirty: true })
          );
        };
        return (
          <Form className="user__settings-form" layout="vertical">
            <FormInput name="firstName" label={t('user:firstName')} useHookForm required />
            <FormInput name="lastName" label={t('user:lastName')} useHookForm required />
            <FormInput
              name="email"
              label={t('user:email')}
              required
              disabled={!!props.user}
              useHookForm
            />
            {!props.user && (
              <FormInput
                name="password"
                label={
                  <React.Fragment>
                    {t('user:password')}
                    <AntTooltip
                      title={<div className="whitespace-pre-line">{t('user:passwordInfo')}</div>}
                      className="self-center ml-1"
                    >
                      <InfoCircleOutlined className="ant-info-tooltip" />
                    </AntTooltip>
                  </React.Fragment>
                }
                required
                useHookForm
              />
            )}
            <div className="user__drawer-button-row">
              <Button className="user__reset-pw-button" onClick={() => handlePasswordReset()}>
                {t('user:resetPassword')}
              </Button>
            </div>

            {!receiverUser && (
              <div className="user__permission-row user__admin-permission flex">
                <div className="user__permission-name">
                  {t('user:admin')}
                  <span className="user__permission-info">
                    <AntTooltip title={t('user:adminInfo')}>
                      <InfoCircleOutlined />
                    </AntTooltip>
                  </span>
                </div>
                <FormSwitch
                  name="admin"
                  checkedChildren={t('common:yes')}
                  unCheckedChildren={t('common:no')}
                  useHookForm
                />
              </div>
            )}
            <div className="user__permission-row flex">
              <div className="user__permission-name">
                {t('accountSettings:form.sendEmailNotification')}
                <span className="user__permission-info">
                  <Tooltip title={t('accountSettings:form.emailNotificationInfoText')}>
                    <InfoCircleOutlined />
                  </Tooltip>
                </span>
              </div>
              <FormSwitch
                name="emailNotification"
                handleChange={(checked: boolean) => {
                  setValue('emailNotification', booleanToInt(checked), { shouldDirty: true });
                }}
                checkedChildren={t('common:yes')}
                unCheckedChildren={t('common:no')}
                useHookForm
              />
            </div>

            {!values.admin && !receiverUser && (
              <React.Fragment>
                <FormMultiSelect
                  name="roles"
                  values={roles}
                  label={t('user:userRole')}
                  placeholder={t('user:selectRoles')}
                  useHookForm
                />
                <div className="user__drawer-button-row">
                  <Button onClick={() => props.showRoleDrawer()}>{t('user:manageRoles')}</Button>
                </div>

                <div className="user__divider_wrapper flex">
                  <div className="flex-1">
                    <Divider className="user__divider" orientation="left">
                      {t('user:allBrandsHeader')}
                    </Divider>
                  </div>
                  <div>
                    <AntSelect
                      onChange={(value: string) => handleBrandSelect(Number(value))}
                      elements={extendedMaintenanceTypes.map(type => ({
                        ...type,
                        id: type.id.toString(),
                      }))}
                      value={brandSelectState(values.brands)}
                      placeholder={t('user:select')}
                    />
                  </div>
                </div>
                {brands.map(brand => (
                  <div key={brand.id} className="user__permission-row flex">
                    <div className="user__permission-name">{brand.name}</div>
                    <FormSelect
                      name={`brands.${brand.id}`}
                      values={extendedMaintenanceTypes}
                      useHookForm
                    />
                  </div>
                ))}
              </React.Fragment>
            )}

            <div className="user__drawer-disable-button">
              {props.user && props.user.id !== props.currentUserId && (
                <Button
                  className="user__cancel-button"
                  onClick={() => handleUserStateChange()}
                  danger
                  ghost
                >
                  {props.user.active ? t('user:setInactive') : t('user:setActive')}
                </Button>
              )}
            </div>
          </Form>
        );
      }}
    </DrawerHookForm>
  );
};

export default UserDrawer;
